import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type RankingSystemSummary,
  type TournamentRankingLinkRow,
} from "../lib/api";
import { isUmbrellaLink, seedingDivisionIdFor, visibleRankingLinks } from "../lib/rankingLinks";

/** R2 del motor de ranking: "Puntúa para…" — liga la categoría del torneo a un
 * sistema de ranking oficial (grado + categoría de edad), recalcula puntos y
 * siembra a los inscritos desde la publicación vigente. Autocontenida para no
 * ensanchar la página de Ranking del wizard. */

const GRADE_OPTIONS: Array<[string, string]> = [
  ["master", "Máster"],
  ["nacional", "Nacional"],
  ["g1", "Grado 1"],
  ["seccional_g2", "Seccional / G2"],
  ["regional", "Regional"],
  ["estatal_g3", "Estatal / G3"],
  ["g4", "Grado 4"],
];

const DRAW_KIND_OPTIONS: Array<[string, string]> = [
  ["main", "Cuadro principal"],
  ["alterno", "Torneo alterno"],
  ["calificacion", "Calificación"],
];

// Feedback Eduardo (2026-07-15): "Tipo de cuadro" no se entendía — cada
// opción explica qué parte del torneo puntúa y con qué tabla del reglamento.
const DRAW_KIND_HINTS: Record<string, string> = {
  main: "El cuadro donde compiten todos: puntúa con la tabla completa del grado (campeón, finalista, semifinales…).",
  alterno: "El torneo de consolación para eliminados temprano: puntúa con la tabla reducida de \"alterno\" del reglamento.",
  calificacion: "La fase previa para ganarse un lugar en el cuadro: cada partido ganado suma puntos fijos (no hay puntos por ronda).",
};

export default function OfficialRankingCard({
  tournamentId,
  divisionId,
  divisionName,
  embedded = false,
  onSeeding,
  seedingDisabled = false,
}: {
  tournamentId: number;
  divisionId: number | null;
  divisionName: string | null;
  /** Paso Ranking: "start" antes de sembrar desde la publicación (el padre pasa
   * a modo manual), "done" al terminar bien (el padre refresca los seeds) y
   * "failed" si no se sembró (el padre restaura su modo). */
  onSeeding?: (phase: "start" | "done" | "failed") => void | Promise<void>;
  /** Paso Ranking: true mientras se guardan seeds (no se puede sembrar). */
  seedingDisabled?: boolean;
  /** true = vive DENTRO de la tarjeta "Ranking oficial" del paso 7 (sección
   * "Avanzado"): sin card propia ni título duplicado — el doble "Ranking
   * oficial" era parte de la confusión que reportó Eduardo (2026-08-30). */
  embedded?: boolean;
}) {
  const { token } = useAuth();
  const [systems, setSystems] = useState<RankingSystemSummary[]>([]);
  const [links, setLinks] = useState<TournamentRankingLinkRow[]>([]);
  const [systemId, setSystemId] = useState<string>("");
  const [categoryCode, setCategoryCode] = useState<string>("");
  const [grade, setGrade] = useState<string>("estatal_g3");
  const [drawKind, setDrawKind] = useState<string>("main");
  // Ranking propio (Eduardo 2026-09-23): multiplicador de este torneo (1 = normal).
  const [multiplier, setMultiplier] = useState<string>("1");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId)) return;
    try {
      const [systemsResp, linksResp] = await Promise.all([
        api.rankings.listSystems(token),
        api.rankings.tournamentLinks(token, tournamentId),
      ]);
      setSystems(systemsResp.data.systems.filter((system) => system.is_active));
      setLinks(linksResp.data.links);
    } catch {
      // Sin permisos o sin sistemas: la tarjeta simplemente no estorba.
      setSystems([]);
      setLinks([]);
    }
  }, [token, tournamentId]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedSystem = systems.find((system) => String(system.id) === systemId) ?? null;
  // Ranking PROPIO del organizador (Eduardo 2026-09-22): puntúa cada partido
  // con sus reglas; grado y tipo de cuadro no aplican.
  const selectedIsCustom = Boolean(selectedSystem?.is_custom);
  // Hallazgo #104: la liga paraguas (torneo completo) también se opera desde
  // la vista de una división.
  const divisionLinks = visibleRankingLinks(links, divisionId);

  async function handleLink() {
    if (!token || !selectedSystem || !categoryCode) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.rankings.linkTournament(token, tournamentId, {
        ranking_system_id: selectedSystem.id,
        category_code: categoryCode,
        grade,
        division_id: divisionId,
        draw_kind: drawKind,
        multiplier: selectedIsCustom ? Math.max(Number(multiplier) || 1, 0.01) : null,
      });
      setMessage("Categoría ligada al ranking. Los resultados capturados ahora generan puntos solos.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRecompute() {
    if (!token) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.rankings.recompute(token, tournamentId);
      setMessage(
        resp.data.frozen
          ? "Torneo cerrado: sus puntos quedaron congelados al cerrarlo. Reábrelo si necesitas recalcular."
          : `Puntos recalculados: ${resp.data.events_created} evento(s) en ${resp.data.links} cuadro(s) ligado(s).`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSeeding(link: TournamentRankingLinkRow) {
    if (!token) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      // El paso Ranking espera aquí el guardado de puntos en vuelo.
      await onSeeding?.("start");
      // Hallazgo #104: desde una vista divisional, el paraguas siembra LA
      // división seleccionada — nunca el torneo completo por accidente.
      const resp = await api.rankings.applyPublicationSeeding(token, tournamentId, {
        ranking_system_id: link.ranking_system_id,
        age_category_id: link.age_category_id,
        division_id: seedingDivisionIdFor(link, divisionId),
      });
      setMessage(
        `Siembra aplicada desde la publicación #${resp.data.publication_id}: ${resp.data.applied} inscrito(s) con posición, ${resp.data.without_position} sin posición publicada (conservan su rank).`,
      );
      void onSeeding?.("done");
    } catch (err) {
      void onSeeding?.("failed");
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  if (systems.length === 0 && links.length === 0) {
    // Embebida: decirlo en vez de desaparecer (el fallo mudo confundía).
    return embedded
      ? <p className="muted">No hay sistemas de puntos disponibles todavía.</p>
      : null;
  }

  const Wrapper = embedded ? "div" : "section";
  return (
    <Wrapper className={`official-ranking-card${embedded ? " official-ranking-card--embedded" : " card"}`}>
      {!embedded && (
        <div className="official-ranking-card__header">
          <h2>Ranking oficial · Puntúa para…</h2>
          <p>
            Liga {divisionName ? `la categoría ${divisionName}` : "este torneo"} a un sistema de puntos
            (p.ej. el ranking FMT). El ranking se actualiza con cada resultado capturado.
          </p>
        </div>
      )}
      {embedded && (
        <div className="official-ranking-card__embedded-head">
          <h3>Sumar puntos a un ranking</h3>
          <span className={`official-ranking-card__chip${divisionLinks.length > 0 ? " is-linked" : ""}`}>
            {divisionLinks.length > 0 ? `Ligado a ${divisionLinks[0].system_name}` : "Sin ligar"}
          </span>
        </div>
      )}
      {embedded && (
        <p className="muted">
          Liga {divisionName ? `la categoría ${divisionName}` : "este torneo"} a un sistema de puntos
          (tu ranking propio o el ranking FMT): cada resultado capturado suma puntos solo.{" "}
          <Link to="/admin/rankings">Administrar mis rankings</Link>
        </p>
      )}

      {divisionLinks.length > 0 && (
        <div className="official-ranking-card__links">
          {divisionLinks.map((link) => (
            <div key={link.link_id} className="official-ranking-card__link">
              <span>
                <strong>{link.system_name}</strong> · {link.category_name}
                {link.grade === "custom"
                  ? ` · ranking propio (puntos por partido${link.multiplier && link.multiplier !== 1 ? ` ×${link.multiplier}` : ""})`
                  : ` · ${GRADE_OPTIONS.find(([value]) => value === link.grade)?.[1] ?? link.grade} · ${
                      DRAW_KIND_OPTIONS.find(([value]) => value === link.draw_kind)?.[1] ?? link.draw_kind
                    }`}
                {isUmbrellaLink(link) && divisionId !== null ? " · liga de torneo completo" : ""}
              </span>
              <span className="official-ranking-card__link-actions">
                <button type="button" className="btn btn--ghost" onClick={() => void handleSeeding(link)} disabled={busy || seedingDisabled}>
                  Sembrar desde publicación
                </button>
                <Link className="btn btn--ghost" to={`/rankings/${link.system_slug}?category=${link.category_code}`}>
                  Ver ranking público
                </Link>
              </span>
            </div>
          ))}
        </div>
      )}

      {systems.length > 0 && (
        <div className="official-ranking-card__form">
          <label>
            Sistema
            <select className="t123-select"
              value={systemId}
              onChange={(e) => {
                setSystemId(e.target.value);
                setCategoryCode("");
              }}
              disabled={busy}
            >
              <option value="">Seleccionar sistema</option>
              {systems.map((system) => (
                <option key={system.id} value={system.id}>{system.name}</option>
              ))}
            </select>
          </label>
          <label>
            {selectedIsCustom ? "Categoría del ranking" : "Categoría de edad"}
            <select className="t123-select" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)} disabled={busy || !selectedSystem}>
              <option value="">Seleccionar</option>
              {(selectedSystem?.categories ?? []).map((category) => (
                <option key={category.code} value={category.code}>{category.name}</option>
              ))}
            </select>
          </label>
          {!selectedIsCustom ? (
            <>
              <label>
                Grado del torneo
                <select className="t123-select" value={grade} onChange={(e) => setGrade(e.target.value)} disabled={busy}>
                  {GRADE_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              <label>
                Tipo de cuadro
                <select className="t123-select" value={drawKind} onChange={(e) => setDrawKind(e.target.value)} disabled={busy}>
                  {DRAW_KIND_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            </>
          ) : null}
          {selectedIsCustom ? (
            <label>
              Multiplicador del torneo
              <input type="number" min={0.01} max={100} step={0.5} value={multiplier} onChange={(e) => setMultiplier(e.target.value)} disabled={busy} />
            </label>
          ) : null}
          <p className="official-ranking-card__hint">
            {selectedIsCustom
              ? `Ranking propio: cada partido paga ${selectedSystem?.rules?.match_win ?? 0} al ganador y ${selectedSystem?.rules?.match_loss ?? 0} al perdedor; en eliminatoria se suman los bonos por posición. Con multiplicador 2, este torneo vale doble.`
              : DRAW_KIND_HINTS[drawKind]}
          </p>
          <div className="official-ranking-card__actions">
            <button type="button" className="btn" onClick={() => void handleLink()} disabled={busy || !selectedSystem || !categoryCode}>
              {busy ? "Guardando…" : "Ligar al ranking"}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => void handleRecompute()} disabled={busy || links.length === 0}>
              Recalcular puntos ahora
            </button>
          </div>
        </div>
      )}

      {message && <p className="form-message">{message}</p>}
      {error && <p className="form-error" role="alert">{error}</p>}
    </Wrapper>
  );
}
